import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  HERKUNFT_BESCHRIFTUNG,
  PROTOKOLL_HERKUNFT,
  PROTOKOLL_PRO_SEITE_STANDARD,
  VORGANGSARTEN,
  VORGANGSART_BESCHRIFTUNG,
  ZEIT_PLATZHALTER,
} from '@mcikm-vote/shared';
import type { ProtokollEintrag, ProtokollFilter } from '@mcikm-vote/shared';

import { protokollExportPfad, useProtokoll } from '../../admin/protokollApi';
import { useVeranstaltungen } from '../../admin/veranstaltungenApi';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';
import { formatiereZeitpunkt } from '../../formatierung';

/**
 * Administratives Änderungsprotokoll (§20.1, §29.3).
 *
 * Die Seite ist **ausschließlich lesend**: Es gibt keine Schaltfläche zum Bearbeiten und keine zum
 * Löschen, weil §29.3 beides ausschließt. Auch ein einzelner Eintrag lässt sich nicht entfernen —
 * die Datenbank weist das zusätzlich per Trigger ab.
 *
 * Die Filter entsprechen genau den sechs aus §29.3. Ein Token erscheint nur maskiert; die
 * Spalte trägt deshalb den Zusatz „maskiert", damit niemand einen vollständigen Token erwartet.
 *
 * Auf schmalen Geräten tritt an die Stelle der Tabelle eine Liste von Karten: Sieben Spalten sind
 * auf einem Smartphone nicht lesbar, und ein waagerecht scrollender Bildschirm ist nach §19.1
 * keine Lösung.
 */
export function ProtokollSeite(): ReactElement {
  const [filter, setFilter] = useState<ProtokollFilter>({});
  const [seite, setSeite] = useState(1);

  const veranstaltungen = useVeranstaltungen(true);
  const abfrage = useProtokoll(filter, seite, PROTOKOLL_PRO_SEITE_STANDARD);

  const daten = abfrage.data;
  const eintraege = daten?.eintraege ?? [];
  const gesamt = daten?.gesamt ?? 0;
  const seiten = Math.max(Math.ceil(gesamt / PROTOKOLL_PRO_SEITE_STANDARD), 1);

  /**
   * Jede Filteränderung springt zurück auf Seite 1 — sonst zeigte die Ansicht eine leere Seite.
   *
   * `undefined` bedeutet hier ausdrücklich „Filter entfernen"; die Eigenschaft wird danach
   * gelöscht, damit `exactOptionalPropertyTypes` und die Abfrage denselben Zustand sehen.
   */
  function aendere(teil: { [K in keyof ProtokollFilter]?: ProtokollFilter[K] | undefined }): void {
    setFilter((bisher) => {
      const neu = { ...bisher, ...teil };
      for (const [schluessel, wert] of Object.entries(neu)) {
        if (wert === undefined) {
          delete (neu as Record<string, unknown>)[schluessel];
        }
      }
      return neu as ProtokollFilter;
    });
    setSeite(1);
  }

  /** Wandelt eine Datumseingabe (`JJJJ-MM-TT`) in einen UTC-Zeitpunkt. */
  function alsZeitpunkt(wert: string, endeDesTages: boolean): string | undefined {
    if (wert === '') {
      return undefined;
    }
    return new Date(`${wert}T${endeDesTages ? '23:59:59.999' : '00:00:00.000'}`).toISOString();
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Änderungsprotokoll</h1>

      <p className="text-text-gedaempft text-sm">
        Das Protokoll wird dauerhaft gespeichert und ist weder bearbeitbar noch einzeln löschbar.
        Tokens erscheinen ausschließlich maskiert; eine konkrete Stimmauswahl wird nie
        protokolliert.
      </p>

      {abfrage.error !== null && abfrage.error !== undefined && (
        <Fehlermeldung>{abfrage.error.message}</Fehlermeldung>
      )}

      <Karte>
        <h2 className="text-base font-semibold">Filter</h2>

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Filterfeld name="protokoll-von" beschriftung="Von">
            <input
              id="protokoll-von"
              type="date"
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
              onChange={(ereignis) => {
                aendere({ von: alsZeitpunkt(ereignis.target.value, false) });
              }}
            />
          </Filterfeld>

          <Filterfeld name="protokoll-bis" beschriftung="Bis">
            <input
              id="protokoll-bis"
              type="date"
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
              onChange={(ereignis) => {
                aendere({ bis: alsZeitpunkt(ereignis.target.value, true) });
              }}
            />
          </Filterfeld>

          <Filterfeld name="protokoll-veranstaltung" beschriftung="Veranstaltung">
            <select
              id="protokoll-veranstaltung"
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
              value={filter.veranstaltungId ?? ''}
              onChange={(ereignis) => {
                aendere({
                  veranstaltungId: ereignis.target.value === '' ? undefined : ereignis.target.value,
                });
              }}
            >
              <option value="">Alle</option>
              {(veranstaltungen.data ?? []).map((veranstaltung) => (
                <option key={veranstaltung.id} value={veranstaltung.id}>
                  {veranstaltung.bezeichnung}
                </option>
              ))}
            </select>
          </Filterfeld>

          <Filterfeld name="protokoll-vorgangsart" beschriftung="Vorgangsart">
            <select
              id="protokoll-vorgangsart"
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
              value={filter.vorgangsart ?? ''}
              onChange={(ereignis) => {
                aendere({
                  vorgangsart:
                    ereignis.target.value === ''
                      ? undefined
                      : (ereignis.target.value as ProtokollFilter['vorgangsart']),
                });
              }}
            >
              <option value="">Alle</option>
              {[...VORGANGSARTEN]
                .sort((a, b) =>
                  VORGANGSART_BESCHRIFTUNG[a].localeCompare(VORGANGSART_BESCHRIFTUNG[b], 'de'),
                )
                .map((art) => (
                  <option key={art} value={art}>
                    {VORGANGSART_BESCHRIFTUNG[art]}
                  </option>
                ))}
            </select>
          </Filterfeld>

          <Filterfeld name="protokoll-erfolg" beschriftung="Ergebnis">
            <select
              id="protokoll-erfolg"
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
              value={filter.erfolg === undefined ? '' : String(filter.erfolg)}
              onChange={(ereignis) => {
                aendere({
                  erfolg:
                    ereignis.target.value === '' ? undefined : ereignis.target.value === 'true',
                });
              }}
            >
              <option value="">Alle</option>
              <option value="true">Nur erfolgreich</option>
              <option value="false">Nur fehlgeschlagen</option>
            </select>
          </Filterfeld>

          <Filterfeld name="protokoll-herkunft" beschriftung="Herkunft">
            <select
              id="protokoll-herkunft"
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
              value={filter.herkunft ?? ''}
              onChange={(ereignis) => {
                aendere({
                  herkunft:
                    ereignis.target.value === ''
                      ? undefined
                      : (ereignis.target.value as ProtokollFilter['herkunft']),
                });
              }}
            >
              <option value="">Alle</option>
              {Object.values(PROTOKOLL_HERKUNFT).map((herkunft) => (
                <option key={herkunft} value={herkunft}>
                  {HERKUNFT_BESCHRIFTUNG[herkunft]}
                </option>
              ))}
            </select>
          </Filterfeld>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-text-gedaempft text-sm">
            {gesamt} {gesamt === 1 ? 'Eintrag' : 'Einträge'}
          </span>

          {/* Downloads über gewöhnliche Verweise: Das Speichern kann der Browser besser. */}
          <a
            className="text-akzent text-sm underline"
            href={protokollExportPfad('pdf', filter)}
            download
          >
            Als PDF herunterladen
          </a>
          <a
            className="text-akzent text-sm underline"
            href={protokollExportPfad('csv', filter)}
            download
          >
            Als CSV herunterladen
          </a>
        </div>
      </Karte>

      <Karte className="overflow-x-auto">
        {abfrage.isPending && eintraege.length === 0 ? (
          <p className="text-text-gedaempft text-sm">Das Protokoll wird geladen …</p>
        ) : eintraege.length === 0 ? (
          <p className="text-text-gedaempft text-sm">Zu diesem Filter gibt es keine Einträge.</p>
        ) : (
          <>
            <table className="hidden w-full text-left text-sm md:table">
              <thead className="border-rand border-b">
                <tr>
                  <th className="py-2 pr-3 font-medium">Zeitpunkt</th>
                  <th className="py-2 pr-3 font-medium">Vorgang</th>
                  <th className="py-2 pr-3 font-medium">Ergebnis</th>
                  <th className="py-2 pr-3 font-medium">Herkunft</th>
                  <th className="py-2 pr-3 font-medium">Token (maskiert)</th>
                  <th className="py-2 pr-3 font-medium">IP-Adresse</th>
                  <th className="py-2 font-medium">Beschreibung</th>
                </tr>
              </thead>
              <tbody>
                {eintraege.map((eintrag) => (
                  <tr key={eintrag.id} className="border-rand/60 border-b align-top">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {formatiereZeitpunkt(eintrag.zeitpunkt)}
                    </td>
                    <td className="py-2 pr-3">{beschriftung(eintrag)}</td>
                    <td className="py-2 pr-3">
                      <ErgebnisZeichen erfolg={eintrag.erfolg} />
                    </td>
                    <td className="py-2 pr-3">
                      {HERKUNFT_BESCHRIFTUNG[eintrag.herkunft] ?? eintrag.herkunft}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {eintrag.tokenMaskiert ?? ZEIT_PLATZHALTER}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {eintrag.ipAdresse ?? ZEIT_PLATZHALTER}
                    </td>
                    <td className="py-2">{eintrag.beschreibung}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <ul className="flex flex-col gap-3 md:hidden">
              {eintraege.map((eintrag) => (
                <li key={eintrag.id} className="border-rand/60 border-b pb-3 last:border-b-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{beschriftung(eintrag)}</span>
                    <ErgebnisZeichen erfolg={eintrag.erfolg} />
                  </div>
                  <p className="text-text-gedaempft mt-1 text-xs">
                    {formatiereZeitpunkt(eintrag.zeitpunkt)} ·{' '}
                    {HERKUNFT_BESCHRIFTUNG[eintrag.herkunft] ?? eintrag.herkunft}
                    {eintrag.tokenMaskiert !== null && ` · ${eintrag.tokenMaskiert}`}
                    {eintrag.ipAdresse !== null && ` · ${eintrag.ipAdresse}`}
                  </p>
                  <p className="mt-1 text-sm">{eintrag.beschreibung}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </Karte>

      {seiten > 1 && (
        <div className="flex items-center justify-between gap-3">
          <Knopf
            art="sekundaer"
            disabled={seite <= 1}
            onClick={() => {
              setSeite((bisher) => Math.max(bisher - 1, 1));
            }}
          >
            Zurück
          </Knopf>
          <span className="text-text-gedaempft text-sm">
            Seite {seite} von {seiten}
          </span>
          <Knopf
            art="sekundaer"
            disabled={seite >= seiten}
            onClick={() => {
              setSeite((bisher) => Math.min(bisher + 1, seiten));
            }}
          >
            Weiter
          </Knopf>
        </div>
      )}
    </div>
  );
}

/**
 * Ein beschriftetes Filterfeld.
 *
 * Beschriftung und Feld sind über `htmlFor` verbunden statt ineinander verschachtelt. Das ist
 * kein Formalismus: Umschließt das `<label>` ein `<select>`, nimmt der zugängliche Name die
 * **Optionstexte** mit auf. Die Auswahl „Vorgangsart" hieße dann für Vorlese- und Prüfwerkzeuge
 * „Vorgangsart Alle Abstimmung als bereit markiert …" — und wäre von „Ergebnis" nicht mehr
 * sauber zu unterscheiden, weil beide Wörter darin vorkommen.
 */
function Filterfeld({
  name,
  beschriftung: text,
  children,
}: {
  name: string;
  beschriftung: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={name} className="font-medium">
        {text}
      </label>
      {children}
    </div>
  );
}

function beschriftung(eintrag: ProtokollEintrag): string {
  return VORGANGSART_BESCHRIFTUNG[eintrag.vorgangsart] ?? eintrag.vorgangsart;
}

/**
 * Erfolg oder Fehlschlag.
 *
 * Nicht allein über Farbe unterschieden (§21): Das Wort steht daneben.
 */
function ErgebnisZeichen({ erfolg }: { erfolg: boolean }): ReactElement {
  return erfolg ? (
    <span className="text-erfolg whitespace-nowrap">✓ erfolgreich</span>
  ) : (
    <span className="text-fehler font-medium whitespace-nowrap">✕ fehlgeschlagen</span>
  );
}
